import { useEffect, useState } from 'react';
import { Box } from '@mantine/core';
import { SLICE, UI } from '../../theme/kioskTheme';

// La ruleta física tiene 12 posiciones, pero el endpoint público devuelve solo
// los premios ACTIVOS: desactivar uno desde el tablero deja 11. La geometría
// tiene que salir de cuántos gajos llegan, no de una constante — antes, con 11,
// caía en el placeholder y se veían 12 gajos sin ningún nombre.
const SLOTS = 12;          // solo para el placeholder de carga
const R = 100;

const RADIO_TEXTO = 47;    // nombre del premio, radial (hacia afuera)
const BANDA_INT = 76;      // banda de marca: borde interno
const BANDA_EXT = 97;      //                 borde externo
const RADIO_BANDA = (BANDA_INT + BANDA_EXT) / 2;

const rad = a => (a - 90) * Math.PI / 180;
const px = (r, a) => R + r * Math.cos(rad(a));
const py = (r, a) => R + r * Math.sin(rad(a));

// Gajo completo
function slicePath(i, DEG) {
  const a0 = i * DEG, a1 = (i + 1) * DEG;
  return `M ${R} ${R} L ${px(R, a0)} ${py(R, a0)} A ${R} ${R} 0 0 1 ${px(R, a1)} ${py(R, a1)} Z`;
}

// Banda de marca: sector anular contra el borde
function bandPath(i, DEG) {
  const a0 = i * DEG, a1 = (i + 1) * DEG;
  return [
    `M ${px(BANDA_EXT, a0)} ${py(BANDA_EXT, a0)}`,
    `A ${BANDA_EXT} ${BANDA_EXT} 0 0 1 ${px(BANDA_EXT, a1)} ${py(BANDA_EXT, a1)}`,
    `L ${px(BANDA_INT, a1)} ${py(BANDA_INT, a1)}`,
    `A ${BANDA_INT} ${BANDA_INT} 0 0 0 ${px(BANDA_INT, a0)} ${py(BANDA_INT, a0)}`,
    'Z',
  ].join(' ');
}

// Los colores salen de la MARCA, no del colorHex de la base: la alternancia
// FORVET/IPG es parte del acuerdo con el socio del stand (§5) y no puede
// romperse porque alguien edite un color desde el admin.
// La banda invierte el valor del gajo para que se despegue de lejos.
const BANDA = {
  FORVET: { fill: '#DCEAF5', text: UI.tinta },   // 6,6:1 contra el gajo navy
  IPG: { fill: '#2D6597', text: '#FFFFFF' },     // 4,1:1 contra el gajo celeste
};

function enDosLineas(txt = '') {
  if (txt.length <= 13) return [txt];
  const palabras = txt.split(' ');
  if (palabras.length === 1) return [txt];
  const mitad = Math.ceil(palabras.length / 2);
  return [palabras.slice(0, mitad).join(' '), palabras.slice(mitad).join(' ')];
}

const PLACEHOLDER = Array.from({ length: SLOTS }, (_, i) => ({
  slot: i + 1, shortLabel: '', brand: i % 2 === 0 ? 'FORVET' : 'IPG',
}));

export function WheelCanvas({ prizes, targetSlot, onSettled, loop = false }) {
  const [rotation, setRotation] = useState(0);
  const [duration, setDuration] = useState(0);
  const reduced = typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Con 2 gajos o más se dibuja lo que haya. El placeholder queda solo para
  // el instante de carga o si el endpoint falla.
  const gajos = (prizes && prizes.length >= 2) ? prizes : PLACEHOLDER;
  const DEG = 360 / gajos.length;

  useEffect(() => {
    if (loop || targetSlot == null) return;

    // El puntero tiene que frenar sobre el GAJO DIBUJADO, no sobre el número
    // de slot. Con los 12 premios activos coinciden; desactivando uno dejan de
    // coincidir y la ruleta frenaba señalando el premio equivocado — peor que
    // un error visible, porque la pantalla del premio decía otra cosa.
    const iGanador = gajos.findIndex(g => g.slot === targetSlot);
    if (iGanador === -1) return;              // el premio no está en la ruleta
    const center = (iGanador + 0.5) * DEG;
    const jitter = reduced ? 0 : (Math.random() * 10 - 5);
    const turns = reduced ? 1 : 5;
    const dur = reduced ? 1 : 3.4;

    setDuration(dur);
    // Acumulativo: normaliza lo que quedo de la vuelta anterior y sigue
    // siempre hacia adelante. Sin esto, el segundo giro frena corrido.
    setRotation(prev => prev + (360 - (prev % 360)) + (360 * turns - center + jitter));

    const t = setTimeout(onSettled, dur * 1000 + 250);
    return () => clearTimeout(t);
  }, [targetSlot, reduced, onSettled, loop, DEG, gajos]);

  return (
    <Box style={{ position: 'relative', width: '100%', aspectRatio: '1' }}>
      <div
        style={{
          position: 'absolute', top: '-1.5%', left: '50%', transform: 'translateX(-50%)',
          width: 0, height: 0, zIndex: 2,
          borderLeft: '15px solid transparent', borderRight: '15px solid transparent',
          borderTop: `28px solid ${UI.accion}`,
          filter: 'drop-shadow(0 2px 3px rgba(23,63,110,.22))',
        }}
      />

      <svg
        viewBox="0 0 200 200"
        className={loop ? 'ruleta-loop' : undefined}
        style={{
          width: '100%', height: '100%',
          ...(loop ? {} : {
            transform: `rotate(${rotation}deg)`,
            transition: `transform ${duration}s cubic-bezier(.15,.9,.25,1)`,
          }),
          filter: 'drop-shadow(0 6px 20px rgba(23,63,110,.14))',
        }}
      >
        {gajos.map((p, i) => {
          const slice = SLICE[p.brand] || SLICE.FORVET;
          const banda = BANDA[p.brand] || BANDA.FORVET;
          const lineas = enDosLineas(p.shortLabel);

          // Premio: RADIAL. El radio del gajo se alinea con el eje +X y el
          // texto se lee del centro hacia afuera.
          const giro = i * DEG + DEG / 2 - 90;
          const premioAlReves = i >= SLOTS / 2;

          // Marca: TANGENCIAL, perpendicular al premio. El centro del gajo se
          // lleva arriba y el texto se escribe horizontal en ese marco.
          const centro = i * DEG + DEG / 2;
          const marcaAlReves = centro > 90 && centro < 270;

          return (
            <g key={p.slot ?? i}>
              <path d={slicePath(i, DEG)} fill={slice.fill} stroke="#FFFFFF" strokeWidth="1.1" />
              <path d={bandPath(i, DEG)} fill={banda.fill} stroke="#FFFFFF" strokeWidth="1.1" />

              <g transform={`rotate(${giro} ${R} ${R})`}>
                <g transform={premioAlReves ? `rotate(180 ${R + RADIO_TEXTO} ${R})` : undefined}>
                  {lineas.map((linea, j) => (
                    <text
                      key={j}
                      x={R + RADIO_TEXTO}
                      y={R + (j - (lineas.length - 1) / 2) * 8}
                      textAnchor="middle" dominantBaseline="middle"
                      fill={slice.text} fontSize="7" fontWeight="700"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      {linea}
                    </text>
                  ))}
                </g>
              </g>

              <g transform={`rotate(${centro} ${R} ${R})`}>
                <g transform={marcaAlReves ? `rotate(180 ${R} ${R - RADIO_BANDA})` : undefined}>
                  <text
                    x={R} y={R - RADIO_BANDA}
                    textAnchor="middle" dominantBaseline="middle"
                    fill={banda.text} fontSize="6.4" fontWeight="800"
                    style={{ fontFamily: 'Montserrat, sans-serif', letterSpacing: '0.06em' }}
                  >
                    {p.brand === 'IPG' ? 'IPG' : 'FORVET'}
                  </text>
                </g>
              </g>
            </g>
          );
        })}

        <circle cx={R} cy={R} r={R - 0.8} fill="none" stroke="#FFFFFF" strokeWidth="1.6" />
        <circle cx={R} cy={R} r="17" fill="#FFFFFF" />
        <circle cx={R} cy={R} r="17" fill="none" stroke={SLICE.IPG.fill} strokeWidth="2.5" />
      </svg>
    </Box>
  );
}
